// JS error + unhandled rejection collector. Drains the stub's early-error
// queue on init and then takes over.
//
// Fingerprinting: a small in-memory cache groups identical errors so a tight
// loop throwing the same message doesn't flood the buffer. We bound the
// cache and fall back to "always send" once it fills.

import { now } from '../safe.js';
import { EVENT_TYPES } from '../schema.js';
import { shouldSample } from '../config.js';

const FP_LIMIT = 64;
const _seen = new Map();              // fp -> count
let _stubEarlyDrained = false;

export function initErrors(viewIdProvider, emit, stubErrorQueue) {
  drainEarly(stubErrorQueue, viewIdProvider, emit);

  window.addEventListener('error', (e) => {
    if (!shouldSample(EVENT_TYPES.ERROR)) return;
    emit(toErrorEvent({
      message: e && e.message,
      source:  e && e.filename,
      lineno:  e && e.lineno,
      colno:   e && e.colno,
      stack:   e && e.error && e.error.stack,
      kind:    'js_error',
      handled: 0
    }, viewIdProvider()));
  }, true);

  window.addEventListener('unhandledrejection', (e) => {
    if (!shouldSample(EVENT_TYPES.ERROR)) return;
    const r = e && e.reason;
    emit(toErrorEvent({
      message: r && (r.message || String(r)),
      source:  '',
      lineno:  0,
      colno:   0,
      stack:   r && r.stack,
      kind:    'unhandled_rejection',
      handled: 0
    }, viewIdProvider()));
  });
}

function drainEarly(stubQueue, viewIdProvider, emit) {
  if (_stubEarlyDrained || !Array.isArray(stubQueue)) return;
  _stubEarlyDrained = true;
  for (const it of stubQueue) {
    emit(toErrorEvent({
      message: it.m,
      source:  it.s,
      lineno:  it.l,
      colno:   it.c,
      stack:   it.st,
      kind:    it.k === 'rejection' ? 'unhandled_rejection' : 'js_error',
      handled: 0,
      ts:      it.t
    }, viewIdProvider()));
  }
  stubQueue.length = 0;
}

function toErrorEvent(x, viewId) {
  const fp = fingerprint(x);
  const seen = _seen.get(fp);
  if (seen != null) {
    _seen.set(fp, seen + 1);
  } else if (_seen.size < FP_LIMIT) {
    _seen.set(fp, 1);
  }
  return {
    id: viewId + ':err:' + fp,
    t: x.ts || now(),
    tp: EVENT_TYPES.ERROR,
    n: x.kind,
    mv: 0,
    mu: '',
    mr: '',
    mnt: '',
    dim: { error_kind: x.kind },
    met: {},
    error_type: x.kind,
    error_message: clip(x.message || '', 512),
    error_stack: clip(x.stack || '', 4096),
    error_source: clip(x.source || '', 256),
    error_line: x.lineno | 0,
    error_column: x.colno | 0,
    error_fingerprint: fp,
    error_handled: x.handled | 0
  };
}

// Fingerprint = first stack frame + message (truncated). Stable enough that
// the same defect groups across reloads; the server can refine it further.
function fingerprint(x) {
  const firstFrame = (x.stack || '').split('\n').find((l) => /at\s|@/.test(l)) || '';
  const norm = (x.message || '').replace(/0x[0-9a-f]+/gi, '0x').replace(/\d+/g, 'N');
  return hash32(norm + '|' + firstFrame.trim());
}

function hash32(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h.toString(16);
}

function clip(s, n) {
  s = String(s);
  return s.length > n ? s.slice(0, n) : s;
}
